import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2, RefreshCw } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lireOtp = () => {
  try {
    return JSON.parse(sessionStorage.getItem("fodindin_otp"));
  } catch {
    return null;
  }
};

export default function VerificationOtp() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(lireOtp);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  if (!otp?.profile_id) {
    return (
      <div className="bg-[#FAF8F5] min-h-screen">
        <Navbar />
        <main className="max-w-md mx-auto px-4 pt-36 pb-24 text-center">
          <p className="text-stone-600 text-sm">Aucune vérification en attente.</p>
        </main>
        <Footer />
      </div>
    );
  }

  const verifier = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/otp/verifier`, { profile_id: otp.profile_id, code });
      toast.success("Numéro vérifié avec succès !");
      sessionStorage.removeItem("fodindin_otp");
      navigate(otp.next || "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de vérification.");
    } finally {
      setLoading(false);
    }
  };

  const renvoyer = async () => {
    try {
      const res = await axios.post(`${API}/otp/renvoyer`, { profile_id: otp.profile_id });
      const maj = { ...otp, demo_code: res.data.demo_code };
      sessionStorage.setItem("fodindin_otp", JSON.stringify(maj));
      setOtp(maj);
      toast.success("Nouveau code envoyé par SMS.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    }
  };

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <main className="max-w-md mx-auto px-4 pt-36 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-[#E05638] text-sm font-semibold tracking-[0.25em] uppercase">Vérification</span>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-stone-900 mt-3 flex items-center gap-3">
            <ShieldCheck size={32} className="text-[#0A5C36]" /> Confirmez votre numéro
          </h1>
          <p className="text-stone-600 mt-3 text-sm leading-relaxed">
            Un code à 6 chiffres vous a été envoyé par SMS. Entrez-le pour activer votre compte.
          </p>
          <form onSubmit={verifier} className="mt-8 space-y-4" data-testid="otp-form">
            <input
              data-testid="otp-code-input"
              inputMode="numeric"
              maxLength={6}
              className="w-full bg-white border border-[#E5DFD5] rounded-xl px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#E05638]/50"
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
            />
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              data-testid="otp-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E05638] hover:bg-[#C84428] disabled:opacity-50 text-white font-semibold py-3.5 rounded-full transition-colors"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Vérifier
            </button>
          </form>
          <button
            onClick={renvoyer}
            data-testid="otp-resend"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-stone-600 hover:text-stone-900 transition-colors"
          >
            <RefreshCw size={14} /> Renvoyer le code
          </button>
          {otp.demo_code && (
            <p className="mt-6 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="otp-demo-code">
              Mode démo (prestataire SMS non branché) — votre code : <b>{otp.demo_code}</b>
            </p>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
